import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import { getParsedItem, showErrorToast } from "../../utils/Functions";
import { addCandidateQuiet, jobListing, uploadResumeQuiet } from "../Career/action";
import {
  BULK_DEFAULTS,
  fileExt,
  matchJobTitle,
  matchResumeFile,
  rowStatus,
  runPool,
  validateFiles,
  validateRow,
} from "./bulkImportUtils";

const STEPS = ["Upload file", "Match resumes", "Review & import"];
const STATUS_LABEL = {
  ready: "Ready",
  fix: "Fix required",
  exists: "Already exists",
  skipped: "Skipped",
};

const norm = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

// Spreadsheet header aliases -> our row fields
const ALIASES = {
  firstName: ["fullname", "name", "candidatename"],
  phone: ["contactnumber", "mobile", "phone", "mobilenumber", "contact"],
  email: ["emailaddress", "email", "emailid"],
  resumeFileName: [
    "resumefilename",
    "resume",
    "resumefile",
    "cv",
    "cvfilename",
    "attachresume",
    "attachedresume",
    "resumeattachment",
    "resumeattached",
  ],
  currentEmployer: ["currentemployer", "employer", "company"],
  jobTitle: [
    "jobtitle",
    "position",
    "positionapplied",
    "appliedjobtitle",
    "assignedjobtitle",
    "designation",
    "role",
  ],
  experience: ["totalexperience", "experience", "totalexperiance"],
};

const toRow = (raw, id) => {
  const byKey = {};
  Object.keys(raw).forEach((k) => {
    byKey[norm(k)] = String(raw[k] ?? "").trim();
  });
  const row = { id };
  Object.keys(ALIASES).forEach((field) => {
    const hit = ALIASES[field].find((a) => byKey[a]);
    row[field] = hit ? byKey[hit] : "";
  });
  row.phone = row.phone.replace(/\D/g, "").slice(-10);
  return row;
};

const BulkImport = ({
  maxFiles = BULK_DEFAULTS.maxFiles,
  maxSizeMB = BULK_DEFAULTS.maxSizeMB,
  concurrency = BULK_DEFAULTS.concurrency,
  onViewCandidates,
}) => {
  const dispatch = useDispatch();
  const jobOpenings = useSelector((state) => state.career?.jobDetailsLising);
  const jobs = Array.isArray(jobOpenings) ? jobOpenings : [];
  useEffect(() => {
    dispatch(jobListing());
  }, [dispatch]);
  const sheetInput = useRef();
  const resumeInput = useRef();
  const folderInput = useRef();
  const rowInput = useRef();
  const [step, setStep] = useState(0);
  const [sheetName, setSheetName] = useState("");
  const [rows, setRows] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [matches, setMatches] = useState({}); // rowId -> file name
  const [results, setResults] = useState({});
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [editing, setEditing] = useState(null);
  const [showResumeDetails, setShowResumeDetails] = useState(false);
  const [unchecked, setUnchecked] = useState({}); // rowId -> true when deselected

  const readSheet = async (file) => {
    if (!file) return;
    if (!["csv", "xlsx", "xls"].includes(fileExt(file.name))) {
      showErrorToast("Please upload a CSV, XLSX or XLS file");
      return;
    }
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = book.Sheets[book.SheetNames[0]];
      const parsed = XLSX.utils
        .sheet_to_json(sheet, { defval: "" })
        .map((raw, i) => toRow(raw, i + 1))
        .filter((r) => r.firstName || r.email || r.phone || r.resumeFileName);
      if (parsed.length === 0) {
        showErrorToast("The spreadsheet has no rows");
        return;
      }
      setSheetName(file.name);
      setRows(parsed);
    } catch (e) {
      showErrorToast("Could not read this spreadsheet");
    }
  };

  const addResumes = (list) => {
    const { accepted, rejected } = validateFiles(Array.from(list || []), [], {
      maxFiles: Infinity,
      maxSizeMB,
    });
    if (rejected.length) {
      showErrorToast(
        `Skipped ${rejected.length} file(s): ${rejected
          .slice(0, 3)
          .map((r) => r.file.name)
          .join(", ")}${rejected.length > 3 ? "…" : ""} (PDF, DOC, DOCX up to ${maxSizeMB}MB)`
      );
    }
    setResumes((prev) => {
      const byName = new Map(prev.map((f) => [f.name, f]));
      accepted.forEach((f) => byName.set(f.name, f));
      return Array.from(byName.values());
    });
    setShowResumeDetails(false);
    return accepted;
  };

  // Per-row pick / drop / paste: load the file and pin it to that row
  const attachResume = (rowId, list) => {
    const accepted = addResumes(list);
    if (accepted.length) setMatches((prev) => ({ ...prev, [rowId]: accepted[0].name }));
  };
  const rowTarget = useRef(null);

  const clearResumes = () => {
    setResumes([]);
    setMatches({});
    setShowResumeDetails(false);
  };

  const startImport = () => {
    importRows(readyRows);
  };

  const clearSheet = () => {
    setSheetName("");
    setRows([]);
    setUnchecked({});
  };

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // Resume file for a row: manual pick wins, otherwise match by sheet filename
  // matches holds manual overrides only: file name, or "" when removed by the user.
  // Everything else is derived from the loaded files so the UI never goes stale.
  const resumeFor = (row) => {
    if (row.id in matches) {
      const picked = matches[row.id];
      return picked && resumes.some((f) => f.name === picked) ? picked : "";
    }
    return matchResumeFile(row.resumeFileName, resumes)?.name || "";
  };

  const matchedNames = new Set(rows.map(resumeFor).filter(Boolean));
  const matchedResumeCount = resumes.filter((f) => matchedNames.has(f.name)).length;
  const unmatchedResumeCount = resumes.length - matchedResumeCount;
  const validCount = rows.filter((r) => rowStatus(r, rows) === "ready").length;
  const errorCount = rows.filter((r) => {
    const s = rowStatus(r, rows);
    return s === "fix" || s === "exists";
  }).length;
  const isChecked = (r) => !unchecked[r.id];
  const selectedCount = rows.filter(isChecked).length;
  const allChecked = rows.length > 0 && selectedCount === rows.length;

  const readyRows = rows.filter((r) => rowStatus(r, rows) === "ready" && isChecked(r));

  const importRows = async (list) => {
    setImporting(true);
    setProgress({ done: 0, total: list.length });
    const user = getParsedItem("userInfo");
    const next = { ...results };
    await runPool(list, concurrency, async (row) => {
      const [firstName, ...rest] = row.firstName.trim().split(/\s+/);
      const file = resumes.find((f) => f.name === resumeFor(row));
      const resume = file ? await uploadResumeQuiet(file) : "";
      next[row.id] = await addCandidateQuiet({
        firstName,
        lastName: rest.join(" "),
        emailId: row.email.trim(),
        phoneCode: "+91",
        mobile: row.phone,
        ...(row.currentEmployer && { currentEmployer: row.currentEmployer }),
        ...(row.experience !== "" && { totalExperiance: row.experience }),
        ...(resume && { resume }),
        consultant: user?.companyId?._id,
        source: user?._id,
        createdFrom: "consultant",
      });
      setResults({ ...next });
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    });
    setImporting(false);
    setFinished(true);
  };

  const reset = () => {
    setStep(0);
    setSheetName("");
    setRows([]);
    setResumes([]);
    setShowResumeDetails(false);
    setMatches({});
    setResults({});
    setFinished(false);
  };

  const imported = rows.filter((r) => results[r.id]?.ok).length;
  const failed = rows.filter((r) => results[r.id] && !results[r.id].ok);
  const downloadFailed = () => {
    const data = failed.map((r) => ({
      Name: r.firstName,
      Email: r.email,
      Phone: r.phone,
      Experience: r.experience,
      Error: results[r.id]?.message || "",
    }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(data), "Failed rows");
    XLSX.writeFile(book, "failed-rows.csv", { bookType: "csv" });
  };
  const importProgress = progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : 0;
  const editErrors = editing ? validateRow(editing) : {};

  const stepsDone = finished;
  const rowsMissingResume = rows.filter((r) => !resumeFor(r)).length;
  const matchComplete = resumes.length > 0 && rows.length > 0 && rowsMissingResume === 0;
  // per-step state: done (green) | warn (orange, resumes loaded but rows unmatched) | active (blue) | ""
  const stepState = [
    rows.length > 0 ? "is-done" : "is-active",
    matchComplete ? "is-done" : resumes.length > 0 ? "is-warn" : "",
    // light until a sheet is uploaded and every row is matched, then dark
    stepsDone ? "is-done" : importing || matchComplete ? "is-active is-current" : "",
  ];

  return (
    <div className="nic-bulk nic-bulk-page">
      <p className="nic-bulk-lead">
        Upload a spreadsheet and resumes — they are matched by filename
        automatically. Review the rows, then import.
      </p>
      <ol className="nic-bulk-steps" aria-label="Import progress">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={stepState[i]}
            aria-current={stepState[i].includes("is-active") ? "step" : undefined}
          >
            <span>{i + 1}</span>
            <b className="nic-bulk-steplabel">{label}</b>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <>
          <div className="nic-bulk-grid">
            <section className="nic-bulk-card">
              <header>
                <h5>
                  Spreadsheet <em>*</em>
                </h5>
                <small>CSV, XLSX or XLS</small>
              </header>
              <div
                className="nic-bulk-drop"
                role="button"
                tabIndex={0}
                aria-label="Choose spreadsheet"
                onClick={() => sheetInput.current.click()}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && sheetInput.current.click()
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  readSheet(e.dataTransfer.files[0]);
                }}
              >
                <span className="bx bx-upload" aria-hidden="true"></span>
                <strong>Drag & Drop Spreadsheet Here</strong>
                <small>
                  Or click to browse • Full Name, Contact Number, Email Address required
                </small>
              </div>
              {sheetName && (
                <div className="nic-bulk-chip">
                  <span className="bx bx-file" aria-hidden="true"></span>
                  <span className="nic-bulk-chipname" title={sheetName}>
                    {sheetName}
                  </span>
                  <span className="nic-bulk-chipcount">{rows.length} rows</span>
                  <button type="button" aria-label="Remove file" onClick={clearSheet}>
                    <span className="bx bx-x" aria-hidden="true"></span>
                  </button>
                </div>
              )}
              <input
                ref={sheetInput}
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  readSheet(e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </section>

            <section className="nic-bulk-card">
              <header>
                <h5>Resume files</h5>
                <small>Optional · PDF, DOC, DOCX · max {maxSizeMB}MB</small>
              </header>
              <div
                className="nic-bulk-drop"
                role="button"
                tabIndex={0}
                aria-label="Choose resume files"
                onClick={() => resumeInput.current.click()}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && resumeInput.current.click()
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  addResumes(e.dataTransfer.files);
                }}
              >
                <span className="bx bx-folder-open" aria-hidden="true"></span>
                <strong>
                  {resumes.length
                    ? `${resumes.length} resume${resumes.length > 1 ? "s" : ""} loaded`
                    : "Drag & Drop Resume Files Here"}
                </strong>
                <small>Match files named in the "ResumeFileName" column</small>
              </div>
              <div className="nic-bulk-cardfoot">
                <button
                  type="button"
                  className="nic-bulk-link"
                  disabled={importing}
                  onClick={() => folderInput.current.click()}
                >
                  Select a folder
                </button>
                {resumes.length > 0 && (
                  <button
                    type="button"
                    className="nic-bulk-link"
                    disabled={importing}
                    onClick={clearResumes}
                  >
                    Clear resumes
                  </button>
                )}
              </div>
              {resumes.length > 0 && (
                <div className="nic-bulk-resume-stats">
                  <span className="nic-bulk-stat">{resumes.length} loaded</span>
                  <span className="nic-bulk-stat is-success">{matchedResumeCount} matched</span>
                  <span className="nic-bulk-stat is-danger">{unmatchedResumeCount} unmatched</span>
                  <button
                    type="button"
                    className="nic-bulk-link"
                    onClick={() => setShowResumeDetails((prev) => !prev)}
                  >
                    {showResumeDetails ? "Hide details" : "Show details"}
                  </button>
                </div>
              )}
              {showResumeDetails && (
                <ul className="nic-bulk-resume-list">
                  {resumes.map((f) => (
                    <li key={f.name} className={matchedNames.has(f.name) ? "is-matched" : "is-unmatched"}>
                      <span title={f.name}>{f.name}</span>
                      <span>{matchedNames.has(f.name) ? "Matched" : "Unmatched"}</span>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={rowInput}
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  attachResume(rowTarget.current, e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={resumeInput}
                type="file"
                hidden
                multiple
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  addResumes(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={folderInput}
                type="file"
                hidden
                webkitdirectory=""
                directory=""
                multiple
                onChange={(e) => {
                  addResumes(e.target.files);
                  e.target.value = "";
                }}
              />
            </section>
          </div>

          {rows.length > 0 && (
            <section className="nic-bulk-card nic-bulk-review">
              <header>
                <h5>Review rows</h5>
                <div className="nic-bulk-pills">
                  <span>{rows.length} parsed</span>
                  <span className="is-valid">{validCount} valid</span>
                  <span className="is-error">{errorCount} with errors</span>
                  <span>{selectedCount} selected</span>
                </div>
              </header>
              <div className="nic-bulk-table-wrap">
                <table className={`nic-bulk-table nic-bulk-review-table${importing ? " is-locked" : ""}`}>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          aria-label="Select all rows"
                          checked={allChecked}
                          onChange={(e) =>
                            setUnchecked(
                              e.target.checked
                                ? {}
                                : Object.fromEntries(rows.map((r) => [r.id, true]))
                            )
                          }
                        />
                      </th>
                      <th>#</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Job Title</th>
                      <th>Resume</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const errs = validateRow(row);
                      const status = rowStatus(row, rows);
                      const resume = resumeFor(row);
                      const cell = (field, type = "text") => (
                        <td>
                          <input
                            type={type}
                            value={row[field] || ""}
                            aria-label={`${field} for row ${i + 1}`}
                            aria-invalid={!!errs[field]}
                            className={errs[field] ? "is-bad" : ""}
                            onChange={(e) => updateRow(row.id, { [field]: e.target.value })}
                          />
                          {errs[field] && <small className="nic-bulk-err">{errs[field]}</small>}
                        </td>
                      );
                      return (
                        <tr key={row.id} className={`is-${status}`}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Select row ${i + 1}`}
                              checked={isChecked(row)}
                              onChange={(e) =>
                                setUnchecked((prev) => ({ ...prev, [row.id]: !e.target.checked }))
                              }
                            />
                          </td>
                          <td>{i + 1}</td>
                          {cell("firstName")}
                          {cell("email", "email")}
                          {cell("phone")}
                          <td>
                            <input
                              type="text"
                              value={row.jobTitle || ""}
                              aria-label={`Job title for row ${i + 1}`}
                              onChange={(e) => updateRow(row.id, { jobTitle: e.target.value })}
                            />
                            {(() => {
                              const m = matchJobTitle(row.jobTitle, jobs);
                              if (!m) return null;
                              return m.matched ? (
                                <small className="nic-bulk-jobmatch is-matched">
                                  Matched ({m.job.title})
                                </small>
                              ) : (
                                <small className="nic-bulk-jobmatch is-unmatched">{m.reason}</small>
                              );
                            })()}
                          </td>
                          <td
                            className="nic-bulk-resumecell"
                            tabIndex={0}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              attachResume(row.id, e.dataTransfer.files);
                            }}
                            onPaste={(e) => attachResume(row.id, e.clipboardData.files)}
                          >
                            {resume ? (
                              <span className="is-found">
                                <span title={resume}>Matched ({resume})</span>
                                <button
                                  type="button"
                                  className="nic-bulk-resume-remove"
                                  aria-label={`Remove resume for row ${i + 1}`}
                                  onClick={() => setMatches((prev) => ({ ...prev, [row.id]: "" }))}
                                >
                                  ×
                                </button>
                              </span>
                            ) : (
                              <>
                                <span className="is-missing">
                                  Not found — choose, drop or paste the resume
                                </span>
                                <button
                                  type="button"
                                  className="nic-bulk-link"
                                  onClick={() => {
                                    rowTarget.current = row.id;
                                    rowInput.current.click();
                                  }}
                                >
                                  Choose / Drop / Paste File
                                </button>
                              </>
                            )}
                          </td>
                          <td>
                            {(() => {
                              const res = results[row.id];
                              if (res) {
                                return (
                                  <span
                                    className={`nic-bulk-badge is-${res.ok ? "ready" : "exists"} is-result`}
                                  >
                                    {res.ok ? "Imported" : `Failed: ${res.message || "Request failed"}`}
                                  </span>
                                );
                              }
                              if (importing && status === "ready" && isChecked(row)) {
                                return <span className="nic-bulk-badge is-ready is-result">Importing…</span>;
                              }
                              return (
                                <span className={`nic-bulk-badge is-${status}`}>
                                  {status === "ready" ? "Pending" : STATUS_LABEL[status]}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {rows.length > 0 && importing && (
            <div className="nic-bulk-actions">
              <div className="nic-bulk-progress" role="status" aria-live="polite">
                <p>
                  <span>Importing candidates…</span>
                  <span>
                    {progress.done}/{progress.total}
                  </span>
                </p>
                <div
                  className="nic-bulk-progress-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={importProgress}
                >
                  <div className="nic-bulk-progress-bar" style={{ width: `${importProgress}%` }} />
                </div>
              </div>
            </div>
          )}
          {rows.length > 0 && !importing && finished && (
            <>
              <div
                className={`nic-bulk-result${failed.length > 0 ? " has-failed" : ""}`}
                aria-live="polite"
              >
                <p className="nic-bulk-summary">
                  <strong>{imported}</strong> imported, <strong>{failed.length}</strong> failed out of{" "}
                  <strong>{rows.length}</strong>.
                </p>
                <div className="nic-bulk-result-actions">
                  {failed.length > 0 && (
                    <button type="button" className="nic-bulk-pill is-outline" onClick={downloadFailed}>
                      Download failed rows
                    </button>
                  )}
                  <button
                    type="button"
                    className="nic-bulk-pill is-dark"
                    onClick={() => onViewCandidates && onViewCandidates()}
                  >
                    Go to Candidates
                  </button>
                </div>
              </div>
            </>
          )}
          {rows.length > 0 && !importing && !finished && (
            <div className="nic-bulk-actions">
              <button
                type="button"
                className="nic-bulk-startover"
                title="Start over"
                onClick={reset}
              >
                Start over
              </button>
              <Button
                variant="dark"
                className="nic-bulk-importbtn"
                disabled={readyRows.length === 0}
                title={readyRows.length === 0 ? "No valid rows selected to import" : "Import candidates"}
                onClick={startImport}
              >
                Import {readyRows.length} Candidate(s)
              </Button>
            </div>
          )}
        </>
      )}

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        {editing && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setRows((prev) => prev.map((r) => (r.id === editing.id ? editing : r)));
              setEditing(null);
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Edit candidate</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {[
                ["firstName", "Full name", "text"],
                ["email", "Email", "email"],
                ["phone", "Phone", "text"],
                ["experience", "Experience (years)", "number"],
              ].map(([key, label, type]) => (
                <label className="nic-bulk-field" key={key}>
                  {label}
                  <input
                    type={type}
                    value={editing[key] || ""}
                    aria-invalid={!!editErrors[key]}
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                  />
                  {editErrors[key] && <small className="text-danger">{editErrors[key]}</small>}
                </label>
              ))}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button variant="dark" type="submit">
                Save
              </Button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default BulkImport;
